import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Popconfirm,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Spin,
  Empty,
  Tooltip,
  Tag,
  Avatar,
  Image
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  UploadOutlined,
  EyeOutlined,
  TeamOutlined,
  IdcardOutlined
  
} from '@ant-design/icons';
import { staffService } from '../../services/staffService';

const { Title, Text } = Typography;
const { TextArea } = Input;

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);

  // Fetch staff data
  const fetchStaff = async () => {
    setLoading(true);
    try {
      const data = await staffService.getAllStaff();
      setStaff(data);
    } catch (error) {
      message.error('Failed to fetch staff data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      const formData = {
        ...values,
        profilePhoto: values.profilePhoto?.[0]?.originFileObj
      };

      if (editingStaff) {
        await staffService.updateStaff(editingStaff._id, formData);
        message.success('Staff member updated successfully');
      } else {
        await staffService.createStaff(formData);
        message.success('Staff member created successfully');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingStaff(null);
      setImageUrl('');
      fetchStaff();
    } catch (error) {
      message.error('Operation failed. Please try again.');
    }
  };

  // Handle edit
  const handleEdit = (record) => {
    setEditingStaff(record);
    setImageUrl(record.profilePhoto || '');
    form.setFieldsValue({
      firstName: record.firstName,
      lastName: record.lastName,
      jobTitle: record.jobTitle,
      description: record.description
    });
    setModalVisible(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    try {
      await staffService.deleteStaff(id);
      message.success('Staff member deleted successfully');
      fetchStaff();
    } catch (error) {
      message.error('Failed to delete staff member');
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalVisible(false);
    setEditingStaff(null);
    setImageUrl('');
    form.resetFields();
  };

  // Upload props
  const uploadProps = {
    beforeUpload: (file) => {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error('You can only upload JPG/PNG files!');
        return false;
      }
      return false; // Prevent auto upload
    },
    onChange: (info) => {
      if (info.fileList.length > 0) {
        const file = info.fileList[0].originFileObj;
        if (file) {
          const reader = new FileReader();
          reader.onload = (e) => setImageUrl(e.target.result);
          reader.readAsDataURL(file);
        }
      }
    },
    maxCount: 1,
    listType: 'picture-card',
    showUploadList: false
  };

  // Statistics
  const stats = {
    total: staff.length,
    active: staff.filter(s => s.jobTitle).length,
    recent: staff.filter(s => {
      const created = new Date(s.createdAt);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return created > weekAgo;
    }).length
  };

  // Table columns
  const columns = [
    {
      title: 'Foto',
      dataIndex: 'profilePhoto',
      key: 'photo',
      render: (photo, record) => (
        <Avatar
          size={50}
          src={photo}
          icon={<UserOutlined />}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            if (photo) {
              Modal.info({
                title: `${record.firstName} ${record.lastName}`,
                content: (
                  <Image
                    width={200}
                    src={photo}
                    alt={`${record.firstName} ${record.lastName}`}
                  />
                ),
                width: 300
              });
            }
          }}
        />
      ),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl']
    },
    {
      title: 'Name',
      dataIndex: 'firstName',
      key: 'name',
      render: (firstName, record) => (
        <div>
          <Text strong>{`${firstName} ${record.lastName}`}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.jobTitle}
          </Text>
        </div>
      ),
      sorter: (a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
      ellipsis: true,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl']
    },
    {
      title: 'Berufsbezeichnung',
      dataIndex: 'jobTitle',
      key: 'jobTitle',
      render: (jobTitle) => (
        <Tag color="green" icon={<IdcardOutlined />}>
          {jobTitle}
        </Tag>
      ),
      responsive: ['sm', 'md', 'lg', 'xl']
    },
    {
      title: 'Beschreibung',
      dataIndex: 'description',
      key: 'description',
      render: (description) => (
        <Text type="secondary">
          {description || 'Keine Beschreibung vorhanden'}
        </Text>
      ),
      ellipsis: true,
      responsive: ['md', 'lg', 'xl']
    },
    {
      title: 'Erstellt',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString('de-DE'),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      responsive: ['md', 'lg', 'xl']
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Details anzeigen">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                Modal.info({
                  title: `${record.firstName} ${record.lastName}`,
                  content: (
                    <div>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Image
                            width={150}
                            src={record.profilePhoto}
                            alt={`${record.firstName} ${record.lastName}`}
                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                          />
                        </Col>
                        <Col span={12}>
                          <p><strong>Name:</strong> {record.firstName} {record.lastName}</p>
                          <p><strong>Berufsbezeichnung:</strong> {record.jobTitle}</p>
                          <p><strong>Beschreibung:</strong> {record.description || 'Keine Beschreibung'}</p>
                          <p><strong>Erstellt:</strong> {new Date(record.createdAt).toLocaleString('de-DE')}</p>
                          <p><strong>Aktualisiert:</strong> {new Date(record.updatedAt).toLocaleString('de-DE')}</p>
                        </Col>
                      </Row>
                    </div>
                  ),
                  width: 500
                });
              }}
            />
          </Tooltip>
          <Tooltip title="Bearbeiten">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Möchten Sie dieses Teammitglied wirklich löschen?"
            onConfirm={() => handleDelete(record._id)}
            okText="Ja"
            cancelText="Nein"
          >
            <Tooltip title="Löschen">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl']
    }
  ];

  return (
    <div style={{ padding: '24px', background: 'var(--app-bg, #f5f5f5)', minHeight: '100vh' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Title level={2} style={{ margin: 0 }}>
              <TeamOutlined style={{ marginRight: 8 }} />
              Teamverwaltung
            </Title>
            <Text type="secondary">Verwalten Sie die Teammitglieder Ihrer Praxis</Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => setModalVisible(true)}
            >
              Teammitglied hinzufügen
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Gesamtanzahl Teammitglieder"
              value={stats.total}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#41b06e' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Aktive Teammitglieder"
              value={stats.active}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Diese Woche hinzugefügt"
              value={stats.recent}
              prefix={<PlusOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Staff Table */}
      <Card>
        <Spin spinning={loading}>
          {staff.length === 0 ? (
            <Empty
              description="Keine Teammitglieder gefunden"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" onClick={() => setModalVisible(true)}>
                Erstes Teammitglied hinzufügen
              </Button>
            </Empty>
          ) : (
            <Table
              columns={columns}
              dataSource={staff}
              rowKey="_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} von ${total} Einträgen`
              }}
              style={{ width: '100%' }}
            />
          )}
        </Spin>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingStaff ? 'Teammitglied bearbeiten' : 'Neues Teammitglied hinzufügen'}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            description: ''
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="Vorname"
                rules={[{ required: true, message: 'Bitte geben Sie den Vornamen ein' }]}
              >
                <Input placeholder="Vorname eingeben" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Nachname"
                rules={[{ required: true, message: 'Bitte geben Sie den Nachnamen ein' }]}
              >
                <Input placeholder="Nachname eingeben" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="jobTitle"
            label="Berufsbezeichnung"
            rules={[{ required: true, message: 'Bitte geben Sie die Berufsbezeichnung ein' }]}
          >
            <Input placeholder="z.B. Medizinische Fachangestellte" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Beschreibung"
          >
            <TextArea
              rows={3}
              placeholder="Beschreibung eingeben (optional)"
            />
          </Form.Item>

          <Form.Item
            name="profilePhoto"
            label="Profilfoto"
            valuePropName="fileList"
            getValueFromEvent={e => Array.isArray(e) ? e : e && e.fileList}
          >
            <Upload
              {...uploadProps}
              listType="picture"
              maxCount={1}
            >
              <div>
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="avatar"
                    style={{ width: '100%', height: '100px', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{ height: '100px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', border: '1px dashed #d9d9d9', borderRadius: '6px' }}>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>Foto hochladen</div>
                  </div>
                )}
              </div>
            </Upload>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleModalClose}>
                Abbrechen
              </Button>
              <Button type="primary" htmlType="submit">
                {editingStaff ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StaffManagement; 